import React from 'react';
import { useCapabilities } from '../../../../hooks/useCapabilities';
import { fetchMe } from '../../../../lib/meClient';
import { canEditTaskWith } from '../../../aufgaben/taskViews';
import { fetchLeadTasks, fetchTaskMembers, updateTask } from '../../../aufgaben/api';
import type { Task, TaskMember } from '../../../aufgaben/types';

/** To-dos einer Anfrage für den Projekt-Verlauf — ab Recht `web.aufgaben.own` (der Server liefert dann nur eigene). */
export function useLeadTasks(leadId: string | null | undefined): {
  tasks: Task[];
  membersById: Map<string, TaskMember>;
  canViewTasks: boolean;
  canEditTask: (task: Task) => boolean;
  toggleDone: (task: Task) => void;
} {
  const { can, loaded } = useCapabilities();
  const canViewTasks = loaded && can('web.aufgaben.own');
  const [userId, setUserId] = React.useState<string | null>(null);
  React.useEffect(() => {
    fetchMe().then((me) => setUserId(me?.user?.id ?? null), () => setUserId(null));
  }, []);
  const canEditTask = React.useCallback(
    (task: Task) =>
      canEditTaskWith(
        {
          userId,
          own: can('web.aufgaben.own'),
          viewAll: can('web.aufgaben.view'),
          editAll: can('web.aufgaben.edit'),
        },
        task,
      ),
    [can, userId],
  );
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [members, setMembers] = React.useState<TaskMember[]>([]);

  React.useEffect(() => {
    if (!canViewTasks || !leadId) {
      setTasks([]);
      return;
    }
    let cancelled = false;
    fetchLeadTasks(leadId).then(
      (items) => !cancelled && setTasks(items),
      () => !cancelled && setTasks([]),
    );
    fetchTaskMembers().then(
      (list) => !cancelled && setMembers(list),
      () => !cancelled && setMembers([]),
    );
    return () => {
      cancelled = true;
    };
  }, [canViewTasks, leadId]);

  const membersById = React.useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const toggleDone = React.useCallback((task: Task) => {
    const done = !task.done;
    const patchLocal = (fn: (t: Task) => Task) => setTasks((prev) => prev.map((t) => (t.id === task.id ? fn(t) : t)));
    patchLocal((t) => ({ ...t, done, done_at: done ? new Date().toISOString() : null }));
    updateTask(task.id, { done }).then(
      (item) => patchLocal(() => item),
      () => patchLocal(() => task),
    );
  }, []);

  return { tasks, membersById, canViewTasks, canEditTask, toggleDone };
}
